/**
 * Enterprise Onboarding Wizard
 * 5-step flow for enterprise-tier customers:
 *   1. Organization Profile
 *   2. Invite Team Members
 *   3. Configure Standards
 *   4. Connect Repository
 *   5. Setup Complete
 *
 * Full-width layout (no AdminLayout sidebar), purple enterprise accent.
 * Adapted from EquilateralAgents EnterpriseOnboarding + MindMeld Onboarding patterns.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useStatus } from '../contexts/StatusContext';
import { enterpriseApi } from '../api/enterprise';
import { standardsApi } from '../api/standards';
import { githubApi, GitHubRepo } from '../api/github';
import type { StandardsCatalog } from '../types';

type Step = 1 | 2 | 3 | 4 | 5;

interface OnboardingStep {
  id: Step;
  title: string;
  description: string;
}

interface TeamInvite {
  email: string;
  role: 'admin' | 'member';
}

const STEPS: OnboardingStep[] = [
  { id: 1, title: 'Organization', description: 'Company profile' },
  { id: 2, title: 'Team', description: 'Invite members' },
  { id: 3, title: 'Standards', description: 'Configure standards' },
  { id: 4, title: 'Repository', description: 'Connect GitHub' },
  { id: 5, title: 'Complete', description: 'Ready to go' },
];

const INDUSTRIES = [
  'Technology',
  'Healthcare',
  'Financial Services',
  'Manufacturing',
  'Retail',
  'Government',
  'Education',
  'Consulting',
  'Other',
];

const TEAM_SIZES = [
  { value: '25-50', label: '25–50' },
  { value: '51-100', label: '51–100' },
  { value: '101-250', label: '101–250' },
  { value: '251-500', label: '251–500' },
  { value: '500+', label: '500+' },
];

const EnterpriseOnboarding: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, client } = useStatus();

  const [step, setStep] = useState<Step>(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Step 1: Organization
  const [companyName, setCompanyName] = useState('');
  const [domain, setDomain] = useState('');
  const [industry, setIndustry] = useState('');
  const [companySize, setCompanySize] = useState('');

  // Step 2: Team Invites
  const [invites, setInvites] = useState<TeamInvite[]>([
    { email: '', role: 'member' },
    { email: '', role: 'member' },
    { email: '', role: 'member' },
  ]);
  const [invitesSent, setInvitesSent] = useState(0);

  // Step 3: Standards
  const [catalog, setCatalog] = useState<StandardsCatalog | null>(null);
  const [enabledCategories, setEnabledCategories] = useState<Record<string, boolean>>({});

  // Step 4: GitHub
  const oauthError = searchParams.get('error');
  const [repos, setRepos] = useState<GitHubRepo[]>([]);
  const [selectedRepo, setSelectedRepo] = useState<GitHubRepo | null>(null);
  const [repoConnected, setRepoConnected] = useState(false);

  const loadRepos = useCallback(async () => {
    setLoading(true);
    setError(null);
    const result = await githubApi.listRepos();
    if (result.success && result.data) {
      setRepos(result.data.repos);
    } else {
      setError(result.error || 'Failed to load repositories');
    }
    setLoading(false);
  }, []);

  // Resume from saved step or handle OAuth callback
  useEffect(() => {
    if (searchParams.get('step') === '4' && searchParams.get('github') === 'connected') {
      setStep(4);
      loadRepos();
      return;
    }
    if (oauthError) {
      setStep(4);
      setError('GitHub connection failed. Please try again.');
      return;
    }
    // Resume from last completed step
    const resumeFromStatus = async () => {
      const result = await enterpriseApi.getOnboardingStatus();
      if (result.success && result.data && result.data.step_completed > 0) {
        const nextStep = Math.min(result.data.step_completed + 1, 5) as Step;
        setStep(nextStep);
      }
    };
    resumeFromStatus();
  }, [searchParams, oauthError, loadRepos]);

  // ---------- Step 1: Organization ----------

  const validateStep1 = (): string | null => {
    if (!companyName.trim()) return 'Organization name is required';
    if (!domain.trim()) return 'Domain is required';
    if (!industry) return 'Please select an industry';
    if (!companySize) return 'Please select team size';
    return null;
  };

  const handleSaveOrg = async () => {
    const validationError = validateStep1();
    if (validationError) {
      setError(validationError);
      return;
    }
    setLoading(true);
    setError(null);

    const result = await enterpriseApi.setupOrganization({
      company_name: companyName,
      domain,
      industry,
      company_size: companySize,
    });

    if (result.success) {
      setStep(2);
    } else {
      setError(result.error || 'Failed to save organization profile');
    }
    setLoading(false);
  };

  // ---------- Step 2: Team Invites ----------

  const addInviteRow = () => {
    if (invites.length < 10) {
      setInvites([...invites, { email: '', role: 'member' }]);
    }
  };

  const updateInvite = (index: number, field: keyof TeamInvite, value: string) => {
    const updated = [...invites];
    updated[index] = { ...updated[index], [field]: value };
    setInvites(updated);
  };

  const removeInvite = (index: number) => {
    if (invites.length > 1) {
      setInvites(invites.filter((_, i) => i !== index));
    }
  };

  const handleSendInvites = async () => {
    const validInvites = invites.filter(inv => inv.email.trim()).map(inv => ({ ...inv, email: inv.email.trim().toLowerCase() }));
    if (validInvites.length === 0) {
      setError('Please enter at least one email address');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    for (const inv of validInvites) {
      if (!emailRegex.test(inv.email)) {
        setError(`Invalid email: ${inv.email}`);
        return;
      }
    }

    if (new Set(validInvites.map(inv => inv.email)).size !== validInvites.length) {
      setError('Each email address may appear only once.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const result = await enterpriseApi.inviteTeamMembers(validInvites);
      if (!result.success) {
        setError(result.error || 'Failed to create invitations');
      } else {
        const created = result.data?.invitations || [];
        const sent = result.data?.sent ?? 0;
        setInvitesSent(previous => previous + sent);
        if (sent === validInvites.length) {
          setStep(3);
        } else {
          const createdEmails = new Set(created.map((inv: { email: string }) => inv.email.toLowerCase()));
          setInvites(validInvites.filter(inv => !createdEmails.has(inv.email)));
          setError(`${sent} of ${validInvites.length} invitations created. Retry the remaining addresses or skip this step.`);
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create invitations');
    } finally {
      setLoading(false);
    }
  };

  const handleSkipInvites = () => {
    setStep(3);
  };

  // ---------- Step 3: Standards ----------

  const loadCatalog = useCallback(async () => {
    setLoading(true);
    const result = await standardsApi.getCatalog();
    if (result.success && result.data) {
      const catalogData = (result.data as any)?.data || result.data;
      setCatalog(catalogData);
      // Pre-enable critical categories
      if (catalogData.categories) {
        const defaults: Record<string, boolean> = {};
        for (const [key, cat] of Object.entries(catalogData.categories)) {
          const category = cat as Record<string, any>;
          defaults[key] = category.critical === true;
        }
        setEnabledCategories(defaults);
      }
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (step === 3 && !catalog) {
      loadCatalog();
    }
  }, [step, catalog, loadCatalog]);

  const toggleCategory = (key: string) => {
    setEnabledCategories(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSaveStandards = async () => {
    // Standards preferences are held in enabledCategories state and
    // persisted when onboarding completes (step 5 → handleGoToDashboard)
    setStep(4);
  };

  // ---------- Step 4: GitHub ----------

  const handleConnectGitHub = async () => {
    setLoading(true);
    setError(null);
    const result = await githubApi.startOAuth();
    if (result.success && result.data) {
      sessionStorage.setItem('github_oauth_state', result.data.state);
      window.location.href = result.data.authorization_url;
    } else {
      setError(result.error || 'Failed to start GitHub connection');
      setLoading(false);
    }
  };

  const handleSelectRepo = (repo: GitHubRepo) => {
    setSelectedRepo(repo);
    setRepoConnected(true);
  };

  const handleSkipRepo = () => {
    setStep(5);
  };

  const handleFinishRepo = () => {
    setStep(5);
  };

  // ---------- Step 5: Complete ----------

  const handleGoToDashboard = async () => {
    setLoading(true);
    setError(null);
    const result = await enterpriseApi.completeOnboarding(enabledCategories);
    if (!result.success) {
      setError(result.error || 'Failed to complete onboarding');
      setLoading(false);
      return;
    }
    navigate('/dashboard', { replace: true });
  };

  // ---------- Navigation ----------

  const handleBack = () => {
    setError(null);
    if (step > 1) {
      setStep((step - 1) as Step);
    }
  };

  const countEnabledCategories = () =>
    Object.values(enabledCategories).filter(Boolean).length;

  // ---------- Render ----------

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900">
            <span className="text-purple-600">Enterprise</span> Setup
          </h1>
          <p className="text-gray-600 mt-2">
            Configure your organization in a few steps
          </p>
        </div>

        {/* Progress Steps */}
        {step < 5 && (
          <div className="mb-8">
            <div className="flex justify-between">
              {STEPS.map((s) => (
                <div key={s.id} className="flex-1 text-center">
                  <div
                    className={`w-10 h-10 rounded-full mx-auto flex items-center justify-center text-sm font-medium ${
                      s.id < step
                        ? 'bg-purple-600 text-white'
                        : s.id === step
                        ? 'bg-purple-600 text-white'
                        : 'bg-gray-200 text-gray-600'
                    }`}
                  >
                    {s.id < step ? (
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    ) : (
                      s.id
                    )}
                  </div>
                  <div className="mt-2">
                    <div className="text-sm font-medium text-gray-900">{s.title}</div>
                    <div className="text-xs text-gray-500 hidden md:block">{s.description}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Step Content */}
        <div className="bg-white rounded-xl shadow-sm p-8">
          {/* ===== Step 1: Organization ===== */}
          {step === 1 && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-gray-900">Organization Information</h2>
              <p className="text-gray-600">Tell us about your company so we can tailor MindMeld for your team.</p>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Organization Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent"
                  placeholder="Acme Corporation"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Company Domain <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={domain}
                  onChange={(e) => setDomain(e.target.value)}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent"
                  placeholder="acme.com"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Your company's primary email domain
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Industry <span className="text-red-500">*</span>
                </label>
                <select
                  value={industry}
                  onChange={(e) => setIndustry(e.target.value)}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent"
                >
                  <option value="">Select industry</option>
                  {INDUSTRIES.map((ind) => (
                    <option key={ind} value={ind}>{ind}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Team Size <span className="text-red-500">*</span>
                </label>
                <select
                  value={companySize}
                  onChange={(e) => setCompanySize(e.target.value)}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent"
                >
                  <option value="">Select team size</option>
                  {TEAM_SIZES.map((size) => (
                    <option key={size.value} value={size.value}>{size.label}</option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* ===== Step 2: Invite Team ===== */}
          {step === 2 && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-gray-900">Invite Team Members</h2>
              <p className="text-gray-600">
                Add your first team members. Admins can manage team settings; members can use MindMeld.
              </p>

              <div className="space-y-3">
                {invites.map((invite, idx) => (
                  <div key={idx} className="flex gap-3 items-center">
                    <input
                      type="email"
                      value={invite.email}
                      onChange={(e) => updateInvite(idx, 'email', e.target.value)}
                      className="flex-1 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent"
                      placeholder="colleague@company.com"
                    />
                    <select
                      value={invite.role}
                      onChange={(e) => updateInvite(idx, 'role', e.target.value)}
                      className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent"
                    >
                      <option value="admin">Admin</option>
                      <option value="member">Member</option>
                    </select>
                    {invites.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeInvite(idx)}
                        className="p-2 text-gray-400 hover:text-red-500"
                        title="Remove"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    )}
                  </div>
                ))}
              </div>

              {invites.length < 10 && (
                <button
                  type="button"
                  onClick={addInviteRow}
                  className="text-sm text-purple-600 hover:text-purple-700 font-medium"
                >
                  + Add another
                </button>
              )}

              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600">
                  Create up to 10 invitations for new accounts. They apply when the invited person confirms signup; this step does not send email. Add existing accounts from Team Members after setup.
                </p>
              </div>
            </div>
          )}

          {/* ===== Step 3: Standards ===== */}
          {step === 3 && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-gray-900">Configure Standards</h2>
              <p className="text-gray-600">
                Choose which standards categories to enable for your organization. Critical standards are always active.
              </p>

              {loading && !catalog && (
                <div className="flex justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                </div>
              )}

              {catalog && catalog.categories && (
                <div className="space-y-3">
                  {Object.entries(catalog.categories).map(([key, cat]) => {
                    const category = cat as Record<string, any>;
                    const isCritical = category.critical === true;
                    const isEnabled = enabledCategories[key] || false;

                    return (
                      <div
                        key={key}
                        onClick={() => !isCritical && toggleCategory(key)}
                        className={`flex items-center justify-between p-4 border rounded-lg cursor-pointer transition-all ${
                          isCritical
                            ? 'border-red-200 bg-red-50'
                            : isEnabled
                            ? 'border-purple-600 bg-purple-50'
                            : 'hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-gray-900">{category.name || key}</span>
                            {isCritical && (
                              <span className="px-2 py-0.5 text-xs font-medium bg-red-100 text-red-800 rounded-full">
                                Critical
                              </span>
                            )}
                            {category.recommended && !isCritical && (
                              <span className="px-2 py-0.5 text-xs font-medium bg-yellow-100 text-yellow-800 rounded-full">
                                Recommended
                              </span>
                            )}
                          </div>
                          {category.description && (
                            <p className="text-sm text-gray-500 mt-1">{category.description}</p>
                          )}
                        </div>
                        <div className="ml-4">
                          <div
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                              isCritical || isEnabled ? 'bg-purple-600' : 'bg-gray-200'
                            }`}
                          >
                            <span
                              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                isCritical || isEnabled ? 'translate-x-6' : 'translate-x-1'
                              }`}
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {catalog && (
                <div className="text-sm text-gray-500">
                  {countEnabledCategories()} categories enabled. You can adjust these later from Settings.
                </div>
              )}
            </div>
          )}

          {/* ===== Step 4: Connect Repository ===== */}
          {step === 4 && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-gray-900">Connect a Repository</h2>
              <p className="text-gray-600">
                Connect a GitHub repository to start discovering patterns and injecting standards.
              </p>

              {!repos.length && !repoConnected && (
                <div className="text-center py-8">
                  <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                    <svg className="w-8 h-8 text-gray-400" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0024 12c0-6.63-5.37-12-12-12z" />
                    </svg>
                  </div>
                  <button
                    onClick={handleConnectGitHub}
                    disabled={loading}
                    className="px-6 py-3 bg-gray-900 text-white rounded-lg hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
                  >
                    {loading ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                        Connecting...
                      </>
                    ) : (
                      <>
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0024 12c0-6.63-5.37-12-12-12z" />
                        </svg>
                        Connect GitHub
                      </>
                    )}
                  </button>
                </div>
              )}

              {repos.length > 0 && !repoConnected && (
                <div className="border rounded-lg overflow-hidden">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Repository</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Language</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Updated</th>
                        <th className="px-6 py-3"></th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {repos.map((repo) => (
                        <tr key={repo.full_name} className="hover:bg-gray-50">
                          <td className="px-6 py-4">
                            <div className="text-sm font-medium text-gray-900">{repo.full_name}</div>
                            {repo.private && (
                              <span className="text-xs text-gray-500">Private</span>
                            )}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-500">{repo.language || '—'}</td>
                          <td className="px-6 py-4 text-sm text-gray-500">
                            {new Date(repo.updated_at).toLocaleDateString()}
                          </td>
                          <td className="px-6 py-4 text-right">
                            <button
                              onClick={() => handleSelectRepo(repo)}
                              className="text-purple-600 hover:text-purple-700 text-sm font-medium"
                            >
                              Select
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {repoConnected && selectedRepo && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center gap-3">
                    <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div>
                      <div className="font-medium text-green-900">Repository connected</div>
                      <div className="text-sm text-green-700">{selectedRepo.full_name}</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ===== Step 5: Complete ===== */}
          {step === 5 && (
            <div className="space-y-6 text-center">
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-purple-100">
                <svg className="h-8 w-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
              </div>

              <h2 className="text-2xl font-bold text-gray-900">You're all set!</h2>
              <p className="text-gray-600 max-w-md mx-auto">
                Your enterprise account is configured and ready to use. Here's a summary of what we set up:
              </p>

              <div className="max-w-md mx-auto text-left space-y-3">
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <svg className="w-5 h-5 text-green-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="text-sm text-gray-900">
                    Organization: <strong>{companyName}</strong>
                  </span>
                </div>

                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <svg className={`w-5 h-5 flex-shrink-0 ${invitesSent > 0 ? 'text-green-600' : 'text-gray-400'}`} fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="text-sm text-gray-900">
                    {invitesSent > 0
                      ? <>{invitesSent} invitation{invitesSent !== 1 ? 's' : ''} created</>
                      : 'No invitations created yet'}
                  </span>
                </div>

                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <svg className="w-5 h-5 text-green-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="text-sm text-gray-900">
                    {countEnabledCategories()} standards categories enabled
                  </span>
                </div>

                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <svg className={`w-5 h-5 flex-shrink-0 ${repoConnected ? 'text-green-600' : 'text-gray-400'}`} fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="text-sm text-gray-900">
                    {repoConnected && selectedRepo
                      ? <>Repository: <strong>{selectedRepo.full_name}</strong></>
                      : 'No repository connected yet'}
                  </span>
                </div>
              </div>

              <div className="pt-4 space-y-3">
                <button
                  onClick={handleGoToDashboard}
                  disabled={loading}
                  className="px-8 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
                >
                  {loading && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>}
                  Go to Dashboard
                </button>
                <div className="flex justify-center gap-6 text-sm">
                  <a href="/users" className="text-purple-600 hover:text-purple-700">Manage Users</a>
                  <a href="/standards" className="text-purple-600 hover:text-purple-700">View Standards</a>
                  <a href="/projects" className="text-purple-600 hover:text-purple-700">View Projects</a>
                </div>
              </div>
            </div>
          )}

          {/* Error Display */}
          {error && (
            <div className="mt-6 bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3 flex-1">
                  <p className="text-sm text-red-700">{error}</p>
                </div>
                <button
                  type="button"
                  onClick={() => setError(null)}
                  className="ml-3 inline-flex rounded-md p-1.5 text-red-500 hover:bg-red-100"
                >
                  <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
            </div>
          )}

          {/* Navigation */}
          {step < 5 && (
            <div className="flex justify-between mt-8 pt-6 border-t">
              <button
                type="button"
                onClick={handleBack}
                disabled={step === 1 || loading}
                className={`px-6 py-2 border rounded-lg ${
                  step === 1 || loading
                    ? 'text-gray-400 cursor-not-allowed'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                Back
              </button>

              <div className="flex gap-3">
                {/* Skip buttons for optional steps */}
                {step === 2 && (
                  <button
                    type="button"
                    onClick={handleSkipInvites}
                    className="px-6 py-2 text-gray-600 hover:text-gray-700"
                  >
                    Skip for now
                  </button>
                )}
                {step === 4 && !repoConnected && repos.length === 0 && (
                  <button
                    type="button"
                    onClick={handleSkipRepo}
                    className="px-6 py-2 text-gray-600 hover:text-gray-700"
                  >
                    Skip for now
                  </button>
                )}

                {/* Primary action buttons */}
                {step === 1 && (
                  <button
                    type="button"
                    onClick={handleSaveOrg}
                    disabled={loading}
                    className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
                  >
                    {loading && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>}
                    Continue
                  </button>
                )}

                {step === 2 && (
                  <button
                    type="button"
                    onClick={handleSendInvites}
                    disabled={loading}
                    className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
                  >
                    {loading && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>}
                    Create Invitations
                  </button>
                )}

                {step === 3 && (
                  <button
                    type="button"
                    onClick={handleSaveStandards}
                    disabled={loading}
                    className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Continue
                  </button>
                )}

                {step === 4 && repoConnected && (
                  <button
                    type="button"
                    onClick={handleFinishRepo}
                    className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                  >
                    Continue
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EnterpriseOnboarding;
